import { Box, Checkbox, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { KpiConfigPeriod } from '../../../data-models/company-financials.data-model';
import { IField, KpiConfigScenario } from '../../../data-models/field2.data-model';
import {
  COMPANY_VIEWS_COMPONENT,
  getKpiDisplayConfig,
  IKpiDisplaySelection,
  updateKpiDisplayConfig,
} from '../../../services/queries/FieldDisplayConfigQueries';
import { createKPI, updateKPI } from '../../../services/queries/KPIConfigQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { transformKpi } from '../../Config/KPI/ConfigKPI';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import {
  ACCRUED_DISPLAY_PERIODS,
  END_OF_PERIOD_DISPLAY_PERIODS,
  kpiDisplayComboKey,
  kpiDisplayComboLabel,
  kpiDisplaySections,
} from './fieldManagerModels';
import { kpiFieldFormStepState } from './KpiFieldFormStateJ';

interface DisplayCombo extends IKpiDisplaySelection {
  key: string;
  label: string;
}

interface KpiFieldDisplayStepProps {
  /** Existing KPI being edited; undefined for create. */
  item?: IField<unknown>;
  /** The form's data snapshot (id + fields the form does not render). */
  formData: Partial<IField<unknown>> | undefined;
}

/**
 * "Set Field Display" step: registers the KPI's Company Views columns in
 * fieldDisplayConfig. The final button first saves the KPI itself, then writes
 * the display registration, then reloads (Field Manager convention).
 */
export function KpiFieldDisplayStep({ item, formData }: KpiFieldDisplayStepProps) {
  const methods = useFormContext<IField<unknown>>();
  const { pushErrorToast, pushSuccessToast, pushWarningToast } = useToastMessageState();
  const [enabled, setEnabled] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(item != null);
  const [loadFailed, setLoadFailed] = useState(false);

  const isEndOfPeriod = methods.getValues().kpiScenario === KpiConfigScenario.EndOfPeriod;
  const formPeriods = useMemo(() => new Set<KpiConfigPeriod>(methods.getValues().periods ?? []), [methods]);
  const sections = useMemo(() => kpiDisplaySections(methods.getValues().sectionType), [methods]);

  // One checkbox per column the KPI will synthesize: (scenario × periods) × sections.
  const combos = useMemo<DisplayCombo[]>(() => {
    const periods = (isEndOfPeriod ? END_OF_PERIOD_DISPLAY_PERIODS : ACCRUED_DISPLAY_PERIODS).filter((p) =>
      formPeriods.has(p)
    );
    return periods.flatMap((period) =>
      sections.map((section) => ({
        period,
        section,
        key: kpiDisplayComboKey(period, section),
        label: kpiDisplayComboLabel(period, section),
      }))
    );
  }, [isEndOfPeriod, formPeriods, sections]);

  useEffect(() => {
    if (!item) {
      return;
    }
    let cancelled = false;
    getKpiDisplayConfig(item.id)
      .then((configs) => {
        if (cancelled) {
          return;
        }
        const companyViews = configs.find((c) => c.component === COMPANY_VIEWS_COMPONENT);
        if (companyViews && companyViews.selections.length > 0) {
          setEnabled(true);
          setSelected(new Set(companyViews.selections.map((s) => kpiDisplayComboKey(s.period, s.section))));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
        }
        pushErrorToast({ message: 'Failed to load the current Company Views display settings.' });
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [item, pushErrorToast]);

  const onToggle = (checked: boolean) => {
    setEnabled(checked);
    if (checked && selected.size === 0) {
      setSelected(new Set(combos.map((c) => c.key)));
    }
  };

  const onToggleCombo = (key: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const stepIsValid = async () => {
    const payload = transformKpi({ ...formData, ...methods.getValues() } as IField<unknown>);
    let saved: IField<unknown>;
    try {
      saved = item ? await updateKPI({ ...payload, id: item.id }) : await createKPI(payload);
    } catch (err) {
      pushErrorToast({ message: getErrorMessage(err, 'Failed to save the KPI.') });
      return false;
    }

    // The prefill failed on edit, so the stored display state is unknown — writing
    // the (empty) switch state would silently deregister the KPI's columns.
    if (item && loadFailed) {
      pushWarningToast({
        message:
          'KPI saved, but the current Company Views display settings could not be loaded, so they were left unchanged. Edit the KPI again to retry.',
      });
      window.location.reload();
      return true;
    }

    const selections: IKpiDisplaySelection[] = enabled
      ? combos.filter((c) => selected.has(c.key)).map(({ period, section }) => ({ period, section }))
      : [];
    try {
      // On create with the switch off there is nothing to deregister — skip the write.
      if (item || selections.length > 0) {
        await updateKpiDisplayConfig(saved.id, { component: COMPANY_VIEWS_COMPONENT, selections });
      }
      pushSuccessToast({ message: item ? 'KPI updated.' : 'KPI created.' });
    } catch (err) {
      pushErrorToast({
        message: getErrorMessage(
          err,
          'KPI saved, but updating Company Views display failed. Edit the KPI to retry.'
        ),
      });
    }
    window.location.reload();
    return true;
  };

  const controlsDisabled = loading || (item != null && loadFailed);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '30rem', py: '1rem' }}>
        <Typography variant='subtitle1'>Set Field Display</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={controlsDisabled}
              onChange={(e) => onToggle(e.target.checked)}
            />
          }
          label='Include in Company Views'
        />
        {item != null && loadFailed && (
          <Typography variant='caption' color='error'>
            The current display settings could not be loaded, so they cannot be changed right now. Saving will
            keep them as they are.
          </Typography>
        )}
        {enabled && (
          <FormGroup sx={{ pl: '2.5rem' }}>
            {combos.map((combo) => (
              <FormControlLabel
                key={combo.key}
                control={
                  <Checkbox
                    checked={selected.has(combo.key)}
                    disabled={controlsDisabled}
                    onChange={(e) => onToggleCombo(combo.key, e.target.checked)}
                  />
                }
                label={combo.label}
              />
            ))}
          </FormGroup>
        )}
      </Box>
      <StepperFormButtons
        stepState={kpiFieldFormStepState}
        stepIsValid={stepIsValid}
        nextButtonLabel={item ? 'Save' : 'Create'}
        nextDisabled={loading || (enabled && combos.every((c) => !selected.has(c.key)))}
      />
    </>
  );
}
